<template>
    <div class="mb-32" :class="position">

        <div class="fixed w-full flex z-50 pointer-events-none" :style="{ right: scrollPadding, paddingLeft: scrollPadding }">

            <div class="w-72"></div>

            <div class="flex-grow px-32">
                <header class="w-full p-16 pointer-events-auto rounded-6 xxl:w-5/6 mx-auto bg-white shadow-md">
                    <div class="flex items-center">
                        <div v-if="canUpdate" class="flex">
                            <simple-dropdown>
                                <template #trigger="{ isOpen }">
                                    <round-button class="mr-4" id="btn-colors" :active="isOpen" icon="colors" />
                                </template>
                                <template #content>
                                    <div class="w-400">
                                        <div class="w-full p-24 flex flex-col">
                                            <span class="mb-8 text-body">
                                                {{ t('site_editor.layout.colors.title') }}
                                            </span>
                                            <span class="text-body-sm text-primary-80">
                                                {{ t('site_editor.layout.colors.description') }}
                                            </span>
                                        </div>
                                        <div class="p-24 bg-primary rounded-b">
                                            <color-selector
                                                :color="form.data.primary_color"
                                                :background="brandHSLA"
                                                class="mb-16"
                                                @color-change="updateColorValue('primary_color', $event)"
                                            >
                                                <template #default>
                                                    {{ t('site_editor.layout.colors.brand.title') }}
                                                </template>
                                                <template #description>
                                                    {{ t('site_editor.layout.colors.brand.description') }}
                                                </template>
                                            </color-selector>
                                            <color-selector
                                                :color="form.data.secondary_color"
                                                :background="textHSLA"
                                                class="mb-16"
                                                @color-change="updateColorValue('secondary_color', $event)"
                                            >
                                                <template #default>
                                                    {{ t('site_editor.layout.colors.font.title') }}
                                                </template>
                                                <template #description>
                                                    {{ t('site_editor.layout.colors.font.description') }}
                                                </template>
                                            </color-selector>
                                        </div>
                                    </div>
                                </template>
                            </simple-dropdown>

                            <simple-dropdown>
                                <template #trigger="{ isOpen }">
                                    <round-button class="mr-4" :active="isOpen" id="btn-fonts" icon="title" />
                                </template>
                                <template #content>
                                    <select-font :form="form" />
                                </template>
                            </simple-dropdown>

                            <simple-dropdown>
                                <template #trigger="{ isOpen }">
                                    <round-button class="mr-4" :active="isOpen" id="btn-corners" icon="border-style" />
                                </template>
                                <template #content>
                                    <div class="w-240 p-24">
                                        <div class="mb-8 text-body-sm">
                                            {{ t('site_editor.layout.corner') }}
                                        </div>
                                        <div>
                                            <range-slider v-model="form.rounding" />
                                        </div>
                                    </div>
                                </template>
                            </simple-dropdown>
                        </div>

                        <div class="flex items-center mx-auto preview-types">
                            <input class="visually-hidden" type="radio" name="preview-type" value="desktop" id="preview-type_desktop" v-model="preview.type" @click="forceRerender" />
                            <label for="preview-type_desktop" class="text-primary-20 mr-16 cursor-pointer">
                                <svg-icon type="computer" class="fill-current w-18 h-18" />
                            </label>

                            <input class="visually-hidden" type="radio" name="preview-type" value="tablet" id="preview-type_tablet" v-model="preview.type" @click="forceRerender" />
                            <label for="preview-type_tablet" class="text-primary-20 mr-16 cursor-pointer">
                                <svg-icon type="tablet" class="fill-current w-18 h-18" />
                            </label>

                            <input class="visually-hidden" type="radio" name="preview-type" value="phone" id="preview-type_phone" v-model="preview.type" @click="forceRerender" />
                            <label for="preview-type_phone" class="text-primary-20 mr-16 cursor-pointer">
                                <svg-icon type="smartphone" class="fill-current w-18 h-18" />
                            </label>
                        </div>

                        <feature ref="feature" feature-key="job-sorting" sub-key="jobs" class="mr-8">
                            <model-sorting
                                v-if="canUpdate"
                                group-id="jobsite"
                                :fetch-models="fetchComponentsForSorting"
                                @update="updateOrderOfComponents"
                            />
                        </feature>
                        <simple-button
                            v-if="canUpdate"
                            display="secondary"
                            look="primary"
                            :disabled="!form.isDirty()"
                            icon="save"
                            @click="saveChanges"
                            :loading="form.submitting"
                        >
                            {{ t('settings.save') }}
                        </simple-button>
                    </div>
                </header>
            </div>
        </div>

        <div class="pt-104 px-32">
            <site-editor :can-edit="canEdit" :can-create="canCreate" />
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';

// Stores and utilities
import { useMainStore } from '../state/store';
import { useAuthenticationStore } from '../state/modules/authentication';
import { useNotificationsStore } from '../state/modules/notifications';
import { useBrandingStore } from '../../shared/state/branding';
import { useRepo } from 'pinia-orm';
import Company from '../models/Company';
import Jobsite from '../models/Jobsite';
import Form from '../etc/forms/Form';
import * as jobsites from '../services/JobsiteService';
import * as locations from '../services/LocationService';
import * as components from '../services/ComponentService';
import { normalizeJson } from '../../shared/helpers';
import { ADD_NOTIFICATION } from '../state/mutation-types';

// Component imports
import SvgIcon from '../components/ui/SvgIcon.vue';
import RoundButton from '../components/ui/RoundButton.vue';
import SimpleDropdown from '../components/ui/SimpleDropdown.vue';
import SiteEditor from '../components/SiteEditor.vue';
import RangeSlider from '../components/ui/RangeSlider.vue';
import ColorSelector from '../components/ui/ColorSelector.vue';
import SelectFont from '../components/ui/SelectFont.vue';
import SimpleButton from '../components/ui/SimpleButton.vue';
import ModelSorting from '../components/ModelSorting.vue';
import Feature from '../components/Feature.vue';

const { t } = useI18n();
const { proxy } = getCurrentInstance();

const props = defineProps({
    position: { type: String, default: 'relative' },
    canEdit: { type: Boolean, default: true },
    canCreate: { type: Boolean, default: true },
});

const mainStore = useMainStore();
const authStore = useAuthenticationStore();
const notificationsStore = useNotificationsStore();
const brandingStore = useBrandingStore();


// Computed
const currentCompany = computed(() => mainStore.currentCompany);
const currentCompanyId = computed(() => authStore.currentCompanyId);
const scrollbarWidth = computed(() => mainStore.scrollbarWidth);
const locale = computed(() => mainStore.locale);
const preview = computed(() => brandingStore.preview);
const brandHSLA = computed(() => brandingStore.brandHSLA);
const textHSLA = computed(() => brandingStore.textHSLA);
const scrollPadding = computed(() => `${scrollbarWidth.value}px`);

const canUpdate = computed(() => {
    if (!props.canEdit) return false;
    return (typeof proxy.$can === 'function' ? proxy.$can('jobsites.update') : true);
});

const company = computed(() => useRepo(Company).find(currentCompanyId.value));

// Form
function generateForm() {
    const jobsite = currentCompany.value.jobsite;
    return new Form([
        { name: 'rounding', value: jobsite.rounding / 100 },
        { name: 'padding', value: jobsite.padding / 100 },
        { name: 'display_font', value: jobsite.display_font },
        { name: 'text_font', value: jobsite.text_font },
        { name: 'primary_color', value: jobsite.primary_color || { h: 180, s: 0.5, l: 0.5, a: 1 } },
        { name: 'secondary_color', value: jobsite.secondary_color || { h: 0, s: 0, l: 0.2, a: 1 } },
    ]);
}
const form = ref(generateForm());

brandingStore.setSettings({
    rounding: form.value.data.rounding,
    padding: form.value.data.padding,
    display_font: form.value.data.display_font,
    text_font: form.value.data.text_font,
    primary_color: form.value.data.primary_color,
    secondary_color: form.value.data.secondary_color,
});

watch(
    () => ({
        rounding: form.value.data.rounding,
        padding: form.value.data.padding,
        display_font: form.value.data.display_font,
        text_font: form.value.data.text_font,
        primary_color: form.value.data.primary_color,
        secondary_color: form.value.data.secondary_color,
    }),
    (newSettings, oldSettings) => {
        if (JSON.stringify(newSettings) !== JSON.stringify(oldSettings)) {
            if (
                (newSettings.primary_color !== form.value.data.primary_color ||
                    newSettings.secondary_color !== form.value.data.secondary_color) &&
                !form.value.submitting
            ) {
                brandingStore.setSettings({
                    rounding: newSettings.rounding,
                    padding: newSettings.padding,
                    display_font: newSettings.display_font,
                    text_font: newSettings.text_font,
                    primary_color: newSettings.primary_color,
                    secondary_color: newSettings.secondary_color,
                });
            }
        }
    },
    { deep: true }
);

// Methods
function saveChanges() {
    form.value.submit((data) =>
        jobsites.update(currentCompanyId.value, {
            data: {
                attributes: {
                    rounding: data.rounding * 100,
                    padding: data.padding * 100,
                    display_font: data.display_font,
                    text_font: data.text_font,
                    primary_color: data.primary_color,
                    secondary_color: data.secondary_color,
                },
            },
        })
    )
        .then((result) => {
            return useRepo(Jobsite).whereId(result.id).update(normalizeJson(result));
        })
        .then(() => {
            notificationsStore[ADD_NOTIFICATION]({
                type: 'success',
                message: t('site_editor.layout.saved'),
            });
        });
}

function fetchComponentsForSorting() {
    return components.index().then((result) => {
        return normalizeJson(result.data)
            .sort((a, b) => a.order - b.order)
            .map((item) => {
                let type = t('site_editor.' + item.type + '.title');
                type = type.charAt(0).toUpperCase() + type.slice(1);
                let title = item.title?.[locale.value] || item.heading?.[locale.value] || '';
                return {
                    id: item.id,
                    label: `<strong>${type}</strong>${title ? ' : ' : ''} ${title}`,
                    active: item.published[locale.value],
                };
            });
    });
}

function updateOrderOfComponents(componentItems) {
    components.updateSort(componentItems).then(() => {
        brandingStore.fetchComponents();
        notificationsStore[ADD_NOTIFICATION]({
            type: 'success',
            message: t('general.order_changed', { subject: t('site_editor.blocks').toLowerCase() }),
        });
    });
}

function updateColorValue(colorType, newColor) {
    const colorCopy = { ...newColor };

    form.value.data[colorType] = colorCopy;

    if (colorType === 'primary_color') {
        brandingStore.setPrimaryColor(colorCopy);
    } else if (colorType === 'secondary_color') {
        brandingStore.setSecondaryColor(colorCopy);
    }
}

onMounted(() => {
    locations.index().then((result) => {
        brandingStore.setAllLocations(normalizeJson(result.data));
    });
    mainStore.setDocumentTitle(t('site_editor.title'));
});
</script>

<style scoped>
.preview-types > input[type="radio"]:checked + label {
    @apply text-secondary;
}
</style>
